import React, { useEffect, useState, useMemo } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./EventCalendar.css";
import axios from "../api/axios";
import {
  format,
  isSameDay,
  parseISO,
  isBefore,
  startOfMonth,
  startOfDay,
} from "date-fns";

const EventCalendar = () => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(startOfMonth(new Date()));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/calendar");
      setEvents(res.data || []);
    } catch (err) {
      console.error("Error fetching calendar events:", err);
    } finally {
      setLoading(false);
    }
  };

  const today = startOfDay(new Date());

  const getEventStatus = (dateString) => {
    const eventDate = parseISO(dateString);
    if (isSameDay(eventDate, today)) return "TODAY";
    if (isBefore(eventDate, today)) return "PAST";
    return "UPCOMING";
  };

  const eventsInCurrentMonth = useMemo(() => {
    return events
      .filter((event) => {
        if (!event.startDate) return false;
        const eventDate = parseISO(event.startDate);
        return (
          eventDate.getMonth() === selectedDate.getMonth() &&
          eventDate.getFullYear() === selectedDate.getFullYear()
        );
      })
      .sort(
        (a, b) =>
          parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime()
      );
  }, [events, selectedDate]);

  const eventsToday = useMemo(() => {
    return events.filter(
      (event) => event.startDate && isSameDay(parseISO(event.startDate), today)
    );
  }, [events, today]);

  const getTileClassName = ({ date, view }) => {
    if (view !== "month") return null;

    const eventsForDate = events.filter(
      (event) => event.startDate && isSameDay(parseISO(event.startDate), date)
    );

    if (eventsForDate.length === 0) return null;

    const status = getEventStatus(eventsForDate[0].startDate);
    if (status === "TODAY") return "tile-today";
    if (status === "PAST") return "tile-past";
    if (status === "UPCOMING") return "tile-future";

    return null;
  };

  const handleActiveStartDateChange = ({ activeStartDate }) => {
    setSelectedDate(startOfMonth(activeStartDate));
  };

  const renderEventCard = (event) => {
    const status = getEventStatus(event.startDate);
    const dateFormatted = format(parseISO(event.startDate), "MMM dd, yyyy");

    return (
      <div key={event.id} className="calendar-event-card glass-panel">
        <div className="event-card-header">
          <h4 className="event-card-title">{event.title}</h4>
          <span className={`status-badge tag-${status.toLowerCase()}`}>
            {status}
          </span>
        </div>
        <p className="event-card-description">{event.description}</p>
        <div className="event-card-date">
          <i className="far fa-calendar-alt"></i>
          <span>{dateFormatted}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <section id="calendar" className="event-calendar-container loading-page">
        <div className="loading-spinner"></div>
        <p>Loading interactive calendar...</p>
      </section>
    );
  }

  return (
    <section id="calendar" className="event-calendar-container">
      <div className="calendar-inner">
        {/* Header */}
        <div className="section-header">
          <span className="section-tag">✦ SCHEDULE &amp; DATES</span>
          <h1 className="section-main-title">Event Calendar</h1>
          <p className="section-description">
            Track all scheduled hackathons, coding workshops, guest lectures, and
            important chapter dates.
          </p>
        </div>

        {/* Calendar + Event List Layout */}
        <div className="calendar-content-wrapper">
          {/* Left Panel: Calendar */}
          <div className="calendar-panel glass-panel">
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              tileClassName={getTileClassName}
              showNeighboringMonth={false}
              next2Label={null}
              prev2Label={null}
              formatMonthYear={(locale, date) => format(date, "MMMM yyyy")}
              onActiveStartDateChange={handleActiveStartDateChange}
              locale="en-US"
            />

            {/* Legend */}
            <div className="calendar-legend">
              <div className="legend-item">
                <span className="dot past"></span>
                <span>Past Events</span>
              </div>
              <div className="legend-item">
                <span className="dot today"></span>
                <span>Today</span>
              </div>
              <div className="legend-item">
                <span className="dot future"></span>
                <span>Upcoming</span>
              </div>
            </div>
          </div>

          {/* Right Panel: Event Cards */}
          <div className="events-panel">
            {/* Events Today */}
            <div className="events-block today-block glass-panel">
              <h3 className="block-title">
                <i className="far fa-clock"></i>
                <span>Events Today</span>
              </h3>
              <div className="event-list">
                {eventsToday.length > 0 ? (
                  eventsToday.map(renderEventCard)
                ) : (
                  <p className="no-events-msg">No events scheduled for today.</p>
                )}
              </div>
            </div>

            {/* Events in Month */}
            <div className="events-block monthly-block glass-panel">
              <h3 className="block-title">
                <i className="fas fa-list-ul"></i>
                <span>Events in {format(selectedDate, "MMMM yyyy")}</span>
              </h3>
              <div className="event-list">
                {eventsInCurrentMonth.length > 0 ? (
                  eventsInCurrentMonth.map(renderEventCard)
                ) : (
                  <p className="no-events-msg">
                    No events scheduled for this month.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventCalendar;